/* ------------------------------------------------------------------
 * BookDetailPage — dedicated V2 route that renders the existing
 * 2×2 quadrant Book Detail (Summary · Key Insights · Action Items ·
 * Core Theme) without taking the user back to the legacy LandingPage.
 *
 * Behaviour parity with /v1?book=<id>:
 *   - same content from bookExamples
 *   - same CSS (.quadrant-modal-* classes from LandingPage.css)
 *   - same "If you remember one thing" block for the 5 showcase books
 *   - same temporary "Begin MyCraftz Journey" bridge CTA → /bodha
 *
 * Visual reskin into the frozen v2 parchment system will be applied
 * later. For now we present the existing experience inside a parchment
 * shell so the user remains in the V2 application.
 * ---------------------------------------------------------------- */
import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  X, BookOpen, Lightbulb, CheckCircle2, Target, Sparkles, ArrowRight, ArrowLeft,
} from 'lucide-react';
import { bookExamples } from '../data/bookExamples';
import { getShowcaseFor } from '../data/mycraftzShowcase';
import PreviewChip from '../components/PreviewChip';
import ParchmentBackground from '../components/ParchmentBackground';
import { startJourney } from '../lib/previewJourney';
import { useLanguage } from '../lib/useLanguage';
import Navigation from '../components/homev3/Navigation';
import { useAuth } from '../context/AuthContext';
import staticBookTranslations from '../i18n/book_translations.json';
import '../styles/LandingPage.css';
import '../styles/BookDetailV2.css';

export default function BookDetailPage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const previewMode = new URLSearchParams(location.search).get('mode') === 'preview';
  const book = bookExamples.find((b) => String(b.id) === String(id));

  // ── Live book-content translation (mirrors v1 LandingPage strategy) ─
  // Two-tier: (1) instant lookup in the pre-translated static bundle,
  // (2) fall back to the /api/translate-book Anthropic endpoint for any
  // book not in the bundle. Results are cached per-session by title+lang.
  const [lang] = useLanguage();
  const { isAuthenticated, loading: authLoading } = useAuth();
  const [translated, setTranslated] = useState(null);
  const [isTranslating, setIsTranslating] = useState(false);
  const [translationError, setTranslationError] = useState(false);
  const cacheRef = useRef({});

  useEffect(() => {
    if (!book) return;
    if (lang === 'en') {
      setTranslated(null);
      setIsTranslating(false);
      setTranslationError(false);
      return;
    }
    const cacheKey = `${book.title}::${lang}`;
    if (cacheRef.current[cacheKey]) {
      setTranslated(cacheRef.current[cacheKey]);
      setIsTranslating(false);
      setTranslationError(false);
      return;
    }
    // Static bundle first — instant, no network.
    const staticEntry = staticBookTranslations?.[book.title]?.[lang];
    if (staticEntry) {
      cacheRef.current[cacheKey] = staticEntry;
      setTranslated(staticEntry);
      setIsTranslating(false);
      setTranslationError(false);
      return;
    }
    // Live fallback — Anthropic via /api/translate-book.
    let cancelled = false;
    setIsTranslating(true);
    setTranslationError(false);
    (async () => {
      try {
        const API_URL = process.env.REACT_APP_BACKEND_URL || '';
        const res = await fetch(`${API_URL}/api/translate-book`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            summary: book.summary,
            insights: book.insights,
            actionItems: book.actionItems,
            coreTheme: book.coreTheme,
            targetLanguage: lang,
            bookTitle: book.title,
          }),
        });
        const data = await res.json();
        if (cancelled) return;
        if (data && data.success) {
          const entry = {
            summary: data.summary,
            insights: data.insights,
            actionItems: data.actionItems,
            coreTheme: data.coreTheme,
          };
          cacheRef.current[cacheKey] = entry;
          setTranslated(entry);
        } else {
          setTranslated(null);
          setTranslationError(true);
        }
      } catch (err) {
        if (!cancelled) {
          setTranslated(null);
          setTranslationError(true);
        }
      } finally {
        if (!cancelled) setIsTranslating(false);
      }
    })();
    return () => { cancelled = true; };
  }, [book, lang]);

  if (!book) {
    return (
      <div
        data-testid="book-detail-notfound"
        style={{
          minHeight: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
          background: '#1a0e07',
          color: '#e9d9b3',
          fontFamily: "'Cormorant Garamond', Georgia, serif",
          padding: 24,
          paddingTop: 80,
          textAlign: 'center',
        }}
      >
        <Navigation />
        <h1 style={{ fontFamily: "'Cinzel', serif", fontSize: '1.6rem', letterSpacing: '.16em' }}>
          BOOK NOT FOUND
        </h1>
        <p style={{ opacity: 0.8 }}>The book you&apos;re looking for isn&apos;t in the iCraftz library yet.</p>
        <button
          onClick={() => navigate('/library')}
          data-testid="back-to-library"
          style={{
            padding: '10px 22px',
            fontFamily: "'Cinzel', serif",
            fontSize: '.78rem',
            letterSpacing: '.16em',
            textTransform: 'uppercase',
            color: '#1a0e07',
            background: 'linear-gradient(180deg, #d8b870 0%, #a37e2a 100%)',
            border: '1px solid #6b4d18',
            borderRadius: 4,
            cursor: 'pointer',
          }}
        >
          ← Back to Library
        </button>
      </div>
    );
  }

  const showcase = getShowcaseFor(book);
  const goToBodha = () => {
    if (showcase) {
      startJourney(showcase.slug); // reset picks/stage — a new journey begins here
      navigate(`/bodha?book=${book.id}`);
    } else {
      navigate(`/bodha?book=${book.id}`);
    }
  };
  const goToLibrary = () => navigate('/library');

  // Effective content — translated fields fall back to English on any gap.
  const content = translated
    ? {
        summary:     translated.summary     || book.summary,
        insights:    translated.insights    || book.insights,
        actionItems: translated.actionItems || book.actionItems,
        coreTheme:   translated.coreTheme   || book.coreTheme,
      }
    : {
        summary:     book.summary,
        insights:    book.insights,
        actionItems: book.actionItems,
        coreTheme:   book.coreTheme,
      };

  return (
    <div
      data-testid="book-detail-page"
      className="quadrant-modal-overlay bookdetail-v2"
      style={{ position: 'static', minHeight: '100vh', paddingTop: 56 }}
    >
      <ParchmentBackground />
      <Navigation />
      {previewMode ? <PreviewChip stage="Discover" /> : null}
      <motion.div
        className="quadrant-modal"
        data-testid="book-modal"
        initial={{ scale: 0.96, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', damping: 26, stiffness: 320 }}
      >
        {/* Back to Library — replaces the legacy ✕ close-modal */}
        <button
          className="quadrant-modal-close"
          onClick={goToLibrary}
          data-testid="modal-back-btn"
          aria-label="Back to Library"
        >
          <ArrowLeft className="w-5 h-5" />
        </button>

        {/* Book Header */}
        <div className="quadrant-modal-header">
          <img
            src={book.image}
            alt={book.title}
            className="quadrant-book-thumbnail"
            onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "data:image/svg+xml,%3Csvg%20xmlns='http://www.w3.org/2000/svg'%20width='120'%20height='160'%3E%3Crect%20width='120'%20height='160'%20fill='%23EADFC4'/%3E%3Crect%20x='34'%20y='40'%20width='52'%20height='80'%20fill='none'%20stroke='%23a37e2a'%20stroke-width='2'/%3E%3Cline%20x1='60'%20y1='40'%20x2='60'%20y2='120'%20stroke='%23a37e2a'%20stroke-width='2'/%3E%3C/svg%3E"; }}
          />
          <div className="quadrant-book-info">
            <h2 className="quadrant-book-title">{book.title}</h2>
            <p className="quadrant-book-author">by {book.author}</p>
            <span className="quadrant-book-category">{book.category}</span>
          </div>
        </div>

        {/* Translation status banner — only shown when actively translating
            via the live API, or when the API failed. Silent when the static
            bundle covers the book (which is the common case). */}
        {lang !== 'en' && (isTranslating || translationError) && (
          <div
            data-testid="translation-status"
            style={{
              margin: '0 0 12px',
              padding: '8px 14px',
              fontFamily: "'Cormorant Garamond', Georgia, serif",
              fontSize: '0.9rem',
              fontStyle: 'italic',
              color: translationError ? '#8B3A1F' : '#5C4A3A',
              background: translationError ? 'rgba(139,58,31,0.08)' : 'rgba(201,168,76,0.10)',
              border: `1px solid ${translationError ? 'rgba(139,58,31,0.35)' : 'rgba(201,168,76,0.45)'}`,
              borderRadius: 4,
              textAlign: 'center',
            }}
          >
            {isTranslating
              ? 'Translating book content — one moment…'
              : 'Translation unavailable right now — showing English.'}
          </div>
        )}

        {/* 2×2 Quadrant Grid */}
        <div className="quadrant-grid">
          <motion.div
            className="quadrant quadrant-summary"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.05 }}
          >
            <div className="quadrant-header">
              <BookOpen className="w-5 h-5" />
              <h3>Summary</h3>
            </div>
            <p className="quadrant-content">{content.summary}</p>
          </motion.div>

          <motion.div
            className="quadrant quadrant-insights"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.10 }}
          >
            <div className="quadrant-header">
              <Lightbulb className="w-5 h-5" />
              <h3>Key Insights</h3>
            </div>
            <ul className="quadrant-list">
              {content.insights.map((insight, idx) => (
                <li key={`insight-${idx}`}>{insight}</li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            className="quadrant quadrant-actions"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
          >
            <div className="quadrant-header">
              <CheckCircle2 className="w-5 h-5" />
              <h3>Action Items</h3>
            </div>
            <ul className="quadrant-list quadrant-actions-list">
              {content.actionItems.map((action, idx) => (
                <li key={`action-${idx}`}>
                  <span className="action-number">{idx + 1}</span>
                  {action}
                </li>
              ))}
            </ul>
          </motion.div>

          <motion.div
            className="quadrant quadrant-theme"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.20 }}
          >
            <div className="quadrant-header">
              <Target className="w-5 h-5" />
              <h3>Core Theme</h3>
            </div>
            <p className="quadrant-theme-text">{content.coreTheme}</p>
          </motion.div>
        </div>

        {/* TEMPORARY BRIDGE CTA — Library → Book Detail → Bodha → … */}
        <motion.div
          className="mc-bodha-bridge"
          data-testid="mc-bodha-bridge"
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.28 }}
          style={{
            margin: '28px 0 4px',
            textAlign: 'center',
            fontFamily: "'Cormorant Garamond', Georgia, serif",
          }}
        >
          <p
            style={{
              margin: '0 0 14px',
              fontSize: '0.95rem',
              color: '#2C1810',
              fontStyle: 'italic',
              letterSpacing: '.01em',
            }}
          >
            {!showcase
              ? 'Discovery is the first stage. Now travel the path from understanding to transformation.'
              : (isAuthenticated || authLoading)
                ? 'Discovery is the first stage. Experience the transformation engine on this book.'
                : 'Discovery is the first stage. Experience the transformation engine on this book — no login required.'}
          </p>
          <button
            onClick={goToBodha}
            data-testid="begin-mycraftz-journey"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              padding: '11px 28px',
              fontFamily: "'Cinzel', serif",
              fontSize: '.82rem',
              fontWeight: 600,
              letterSpacing: '.18em',
              textTransform: 'uppercase',
              color: '#1a0e07',
              background: 'linear-gradient(180deg, #d8b870 0%, #a37e2a 100%)',
              border: '1px solid #6b4d18',
              borderRadius: 4,
              boxShadow:
                '0 4px 14px rgba(31,17,8,.28), inset 0 1px 0 rgba(255,239,200,.45)',
              cursor: 'pointer',
            }}
          >
            Begin MyCraftz {showcase ? 'Preview' : 'Journey'} <ArrowRight size={14} />
          </button>
        </motion.div>

        {/* IF YOU REMEMBER ONE THING — only for the 5 MyCraftz showcase books */}
        {showcase && (
          <motion.div
            className="mc-remember-block"
            data-testid="mc-remember-block"
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.34 }}
          >
            <p className="mc-remember-eyebrow">
              <Sparkles size={12} /> If you remember one thing…
            </p>
            <blockquote className="mc-remember-quote">{showcase.rememberOneThing}</blockquote>
            <div className="mc-remember-foot">
              <p className="mc-remember-meta">
                The installable idea: <strong>{showcase.ideaName}</strong>
              </p>
            </div>
          </motion.div>
        )}
      </motion.div>
    </div>
  );
}
